/**
 * Entfernungen, Kurse und eine örtliche Ebene.
 *
 * Alles hier rechnet ohne Datenbank und ohne Browser. Die Erde wird als Kugel
 * angenommen — auf den Entfernungen einer Ausfahrt liegt der Fehler gegenüber
 * dem Ellipsoid unter einem halben Promille und damit weit unter dem, was GPS
 * im Auto liefert.
 */
/** Mittlerer Erdradius nach IUGG, in Metern. */
export const EARTH_RADIUS_M = 6_371_008.8;
export function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
}
export function toDegrees(radians) {
    return (radians * 180) / Math.PI;
}
/** Entfernung zweier Punkte über die Kugel, in Metern. */
export function distanceM(a, b) {
    const φ1 = toRadians(a.lat);
    const φ2 = toRadians(b.lat);
    const Δφ = toRadians(b.lat - a.lat);
    const Δλ = toRadians(b.lon - a.lon);
    const h = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}
/** Kurs von a nach b in Grad, 0 ist Norden. */
export function bearingDeg(a, b) {
    const φ1 = toRadians(a.lat);
    const φ2 = toRadians(b.lat);
    const Δλ = toRadians(b.lon - a.lon);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}
/**
 * Punkt, der von `from` aus in der angegebenen Richtung und Entfernung liegt.
 *
 * Wird an zwei Stellen gebraucht: der Simulator bewegt seine Fahrzeuge damit
 * die Strecke entlang, und die Weboberfläche schreibt zwischen zwei Meldungen
 * die Bewegung fort, damit die Fahrzeuge gleiten statt zu springen.
 */
export function destination(from, bearing, distance) {
    const δ = distance / EARTH_RADIUS_M;
    const θ = toRadians(bearing);
    const φ1 = toRadians(from.lat);
    const λ1 = toRadians(from.lon);
    const sinφ2 = Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
    const φ2 = Math.asin(Math.min(1, Math.max(-1, sinφ2)));
    const λ2 = λ1 +
        Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * sinφ2);
    return {
        lat: toDegrees(φ2),
        // Auf den Bereich von -180 bis 180 zurückholen.
        lon: (((toDegrees(λ2) + 540) % 360) - 180),
    };
}
export function localFrame(origin) {
    const metersPerDegreeLat = (Math.PI / 180) * EARTH_RADIUS_M;
    return {
        lat0: origin.lat,
        lon0: origin.lon,
        metersPerDegreeLat,
        metersPerDegreeLon: metersPerDegreeLat * Math.cos(toRadians(origin.lat)),
    };
}
export function toLocal(frame, point) {
    return {
        x: (point.lon - frame.lon0) * frame.metersPerDegreeLon,
        y: (point.lat - frame.lat0) * frame.metersPerDegreeLat,
    };
}
export function fromLocal(frame, point) {
    return {
        lat: frame.lat0 + point.y / frame.metersPerDegreeLat,
        lon: frame.lon0 + point.x / frame.metersPerDegreeLon,
    };
}
//# sourceMappingURL=distance.js.map